<template>
  <div class="signup-container">
    <!-- Left side with logo and intro -->
    <div class="signup-intro">
      <img class="logo" src="/assets/img/Blits-logo-black.png" />
      <h2>Get instant access to a huge ecosystem of chat & voicebot technology</h2>
      <p>Start your digital transformation journey today</p>
      <img class="signup-image" src="/assets/img/blits-box-img.png" />
    </div>

    <!-- Right side with form -->
    <div class="signup-form">
      <!-- Success message -->
      <div v-if="signupSuccess" class="success-message">
        <h2>Account created</h2>
        <img src="/assets/img/signup-success-icon.png" />
        <p>Check your email to complete the process</p>
      </div>

      <!-- Form fields -->
      <div v-else class="form-content">
        <!-- Form header -->
        <h2 v-if="!activation_email && !this.data.customPlanName">Create an account for blits.ai</h2>
        <h2 v-else-if="this.title">{{ this.title }}</h2>
        <h2 v-else>Complete your AppSumo signup</h2>
        <p v-if="!ssoCreation" class="subheader">Start building within 5 min</p>

        <!-- Form fields -->
        <div class="form-field">
          <label for="invitation-code">Invitation code</label>
          <input v-model="data.invitationCode" type="text" id="invitation-code" placeholder="Your received invitation code" />
          <div v-show="invitationCodeError" class="error">You need an invitation code to create an account</div>
        </div>

        <div class="form-field">
          <label for="name">Your Name</label>
          <input v-model="data.name" type="text" id="name" placeholder="Dean Kitman" />
          <div v-show="nameError" class="error">Please enter a valid name</div>
        </div>

        <div class="form-field">
          <label for="organization">Organization Name</label>
          <input v-model="data.organization" type="text" id="organization" placeholder="Movers Inc." />
          <div v-show="organizationError" class="error">Please enter a valid organization name</div>
        </div>

        <div class="form-field">
          <label for="work-email">{{ createAccountEmailText }}</label>
          <input v-model="data.workEmail" type="email" id="work-email" placeholder="D.Kitman@movers.inc" :disabled="activation_email" />
          <div v-show="emailError" class="error">Please enter a valid email</div>
        </div>

        <template v-if="!ssoCreation">
          <div class="form-field">
            <label for="password">Password</label>
            <input v-model="data.password" type="password" id="password" placeholder="Pick a strong password" />
            <div v-show="passwordError" class="error">Password must at least be 8 characters long</div>
          </div>

          <div class="form-field">
            <label for="confirm-password">Re-enter Password</label>
            <input v-model="data.confirmPassword" type="password" id="confirm-password" placeholder="Enter the same password" />
            <div v-show="confirmPasswordError" class="error">The passwords do not match</div>
          </div>
        </template>

        <!-- Submit button -->
        <SmallLoading v-if="isCreating" class="loading"></SmallLoading>
        <button v-else class="submit-button" @click="submit(ssoCreation ? identity : null)">
          <i :class="createAccountIcon" v-if="ssoCreation"></i>{{ createAccountButton }}
        </button>

        <!-- Alternative sign-up options -->
        <div v-if="!ssoCreation" class="alt-signup">
          <div class="divider"><span>or</span></div>
          <p>signup using an existing</p>
          <button class="microsoft-button" @click="signUpWithMicrosoft()">
            <i class="icon icon-microsoft"></i>
            Microsoft Account
          </button>
        </div>

        <p class="login-link">Already have an account, login <a href="/login">here</a></p>
      </div>

      <!-- Terms of service -->
      <p class="terms">
        By signing up, you accept the
        <a href="https://www.blits.ai/terms-and-conditions/" target="_blank" rel="noopener noreferrer">Blits Terms of Service</a>, acknowledge the
        <a href="https://www.blits.ai/privacy-policy/" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and the
        <a href="https://www.blits.ai/EULA" target="_blank" rel="noopener noreferrer">End user license agreement</a>.
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Mixins } from 'vue-property-decorator'
import Select from '../helpers/Select.vue'
import SmallLoading from '../helpers/SmallLoading.vue'
import { countryNamesByCode } from '../../../../common/constants/countries.constant'
import usersService from '../../services/users.service'
import UserSignUpDto from '../../../../common/interfaces/user-signup-dto.interface'
import popupService from '../../services/popup.service'
import { UserIdentity, USER_IDENTITY_PLATFORM } from '../../../../common/types/users/user-identity.type'
import authManagmentMixin from './auth-managment.mixin'
import { validateEmail, validatePassword, validateUserName, validateOrganizationName, validateStringOnly } from '../../../../common/validators/validator'

@Component({
  components: {
    Select,
    SmallLoading
  }
})
export default class SignUp extends Mixins(authManagmentMixin) {
  private signupSuccess = false
  private nameError = false
  private emailError = false
  private organizationError = false
  private passwordError = false
  private confirmPasswordError = false
  private invitationCodeError = false
  private ssoCreation = false
  private isCreating = false
  private createAccountHeading = 'Create an account for blits.ai'
  private createAccountButton = 'Create Account'
  private createAccountEmailText = 'Work email'
  private createAccountIcon = ''
  private identity = ''

  private data: UserSignUpDto = {
    identity: USER_IDENTITY_PLATFORM,
    name: '',
    organization: '',
    workEmail: '',
    country: '',
    password: '',
    confirmPassword: '',
    customPlanName: '',
    couponId: '',
    invitationCode: ''
  }

  activation_email = ''
  title = ''

  async beforeMount() {
    await this.checkAuth()
    this.routerCheck()
  }

  async created() {
    this.activation_email = this.$route.params.activation_email
    this.title = this.$route.params.title
    this.data.workEmail = this.activation_email
    this.data.customPlanName = this.$route.params.prod_name
    this.data.couponId = this.$route.params.coupon_id
    this.data.country = countryNamesByCode[(await usersService.findUserLocation()).country]
  }

  private async submit(identity?: UserIdentity) {
    try {
      identity ? (this.data.identity = identity) : undefined
      const isIdentityPlatform = this.data.identity == USER_IDENTITY_PLATFORM
      this.validateFields()
      const error =
        this.nameError ||
        this.emailError ||
        this.invitationCodeError ||
        this.organizationError ||
        (isIdentityPlatform ? this.passwordError || this.confirmPasswordError : false)
      if (!error) {
        this.isCreating = true
        const res = await usersService.signUp(this.data)
        if (res.status == 200 && isIdentityPlatform) {
          this.signupSuccess = true
          this.isCreating = false
        } else {
          window.open(res.data, '_self')
        }
      }
    } catch (e) {
      popupService.showError('Error occured while signing up', e.message)
      this.isCreating = false
    }
  }

  private signUpWithMicrosoft() {
    this.ssoCreation = true
    this.createAccountHeading = 'Sign up with your Microsoft Account'
    this.createAccountButton = 'Sign up with Microsoft'
    this.identity = 'microsoft'
    this.createAccountIcon = 'icon icon-microsoft'
    this.createAccountEmailText = 'Microsoft account email'
  }

  private validateFields() {
    this.validateInvitationCodeField()
    this.validateNameField()
    this.validateOrganizationField()
    this.validateEmailField()
    this.validatePasswordField()
    this.validateConfirmPasswordField()
  }

  private validateNameField() {
    this.nameError = !validateUserName(this.data.name)
  }

  private validateOrganizationField() {
    this.organizationError = !validateOrganizationName(this.data.organization)
  }

  private validateEmailField() {
    this.emailError = !validateEmail(this.data.workEmail)
  }

  private validatePasswordField() {
    this.passwordError = !validatePassword(this.data.password)
  }

  private validateConfirmPasswordField() {
    this.confirmPasswordError = this.data.confirmPassword != this.data.password
  }

  private validateInvitationCodeField() {
    this.invitationCodeError = !validateStringOnly(this.data.invitationCode)
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables';

.signup-container {
  display: flex;
  justify-content: center;
  align-items: stretch;
  max-width: 1200px;
  margin: 3% auto;
  background-color: white;
  border-radius: 6px;
  min-height: 600px;
  box-shadow: 0 2px 20px rgba(31, 31, 31, 0.04);

  @media (max-width: 768px) {
    flex-direction: column;
  }
}

.signup-intro {
  flex: 1;
  padding: 40px;
  text-align: center;

  .logo {
    max-width: 120px;
    margin-bottom: 20px;
  }

  h2 {
    font-weight: 700;
    font-size: 25px;
    margin-bottom: 15px;
  }

  p {
    font-size: 16px;
    color: $secondary-text;
    margin-bottom: 20px;
  }

  .signup-image {
    max-width: 100%;
    max-height: 300px;
  }
}

.signup-form {
  flex: 1;
  padding: 40px;
  border-left: 1px solid $light-blue;

  @media (max-width: 768px) {
    border-left: none;
    border-top: 1px solid $light-blue;
  }

  .success-message {
    text-align: center;
    padding: 20px;

    h2 {
      font-size: 24px;
      margin-bottom: 20px;
    }

    img {
      max-width: 200px;
      margin: 20px auto;
    }

    p {
      font-size: 16px;
    }
  }

  .form-content {
    h2 {
      font-size: 24px;
      font-weight: 700;
      margin-bottom: 10px;
    }

    .subheader {
      font-size: 16px;
      margin-bottom: 20px;
    }
  }

  .form-field {
    margin-bottom: 15px;

    label {
      display: block;
      font-weight: 700;
      font-size: 14px;
      margin-bottom: 5px;
    }

    input {
      width: 100%;
      height: 40px;
      padding: 0 15px;
      border: 1px solid $light-blue;
      border-radius: 5px;

      &:focus {
        border-color: $main;
        outline: none;
      }
    }

    .error {
      color: red;
      font-size: 12px;
      margin-top: 5px;
    }
  }

  .submit-button {
    width: 100%;
    height: 40px;
    margin: 20px 0;
    background: $main;
    border: none;
    border-radius: 5px;
    color: white;
    font-weight: 700;
    cursor: pointer;

    &:hover {
      opacity: 0.9;
    }

    i {
      margin-right: 5px;
    }
  }

  .alt-signup {
    margin: 20px 0;
    text-align: center;

    .divider {
      position: relative;
      margin: 15px 0;

      &:before {
        content: '';
        position: absolute;
        top: 50%;
        left: 0;
        width: 100%;
        height: 1px;
        background-color: $secondary-text;
      }

      span {
        position: relative;
        background-color: white;
        padding: 0 10px;
        color: $secondary-text;
      }
    }

    p {
      font-size: 14px;
      margin-bottom: 10px;
    }

    .microsoft-button {
      background: transparent;
      border: none;
      color: $main;
      font-weight: 700;
      cursor: pointer;
      text-decoration: underline;

      .icon-microsoft {
        display: inline-block;
        background-image: url('/public/assets/icons/icon-logo-ms.png');
        background-size: contain;
        background-repeat: no-repeat;
        width: 20px;
        height: 20px;
        vertical-align: middle;
        margin-right: 5px;
      }
    }
  }

  .login-link {
    font-size: 13px;
    text-align: center;
    margin: 15px 0;

    a {
      color: $main;
      font-weight: 700;
    }
  }

  .terms {
    font-size: 11px;
    text-align: center;
    margin-top: 20px;

    a {
      color: $main;
    }
  }

  .loading {
    display: block;
    margin: 20px auto;
  }
}
</style>
